import React, { FC, ReactNode, useEffect, useState } from 'react';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import FolderCopyIcon from '@mui/icons-material/FolderCopy';
import { styled } from '@mui/system';
import Select, { getSelectValue, SelectedItemsType, SelectedNodesType, SelectNodeValueType } from './Select';
import CircularProgress from '@mui/material/CircularProgress';
import MUIBox from '@mui/material/Box';
import { colors, sizes } from 'src/themes/default';
import { ThreeStateValueType } from '../ThreeStateCheckbox';
import { SxProps, Theme } from '@mui/system';

export type ReceivedRowType = { [key: string]: string | any };

export type TreeNodeType = { isEndpoint: boolean; data: any } | any;

const TREE_META_KEYS = new Set(['data', 'isEndpoint']);

/** Child node keys only (excludes endpoint metadata on hybrid nodes). */
export const getChildKeys = (treeNode: TreeNodeType): string[] =>
    Object.keys(treeNode).filter((key) => !TREE_META_KEYS.has(key));

const setInTree = (tree: TreeNodeType, keys: string[], row: ReceivedRowType) => {
    if (keys.length > 1) {
        if (!tree[keys[0]]) {
            tree[keys[0]] = {};
        }
        setInTree(tree[keys[0]], keys.slice(1), row);
    } else if (!tree[keys[0]]) {
        tree[keys[0]] = { data: row, isEndpoint: true };
    } else {
        // Keep existing children when a shorter PID is also a real permission
        // (e.g. rare hybrid nodes where a prefix PID exists alongside longer ones).
        tree[keys[0]].data = row;
        tree[keys[0]].isEndpoint = true;
    }
};

const arrToTreeStructure: TreeNodeType = (rows: ReceivedRowType[], key: string) => {
    const tree: TreeNodeType = {};
    rows.forEach((row) => {
        const arr = row[key].split('.');
        setInTree(tree, arr, row);
    });
    return tree;
};

const TreeContainer = styled('div')({
    paddingTop: sizes.body_PD,

    '& .MuiTreeItem-root.tree-item-selected-1': {
        '& > .MuiTreeItem-content': {
            backgroundColor: colors.primary_1,
        },
    },
    '& .MuiTreeItem-root.tree-item-selected--1': {
        '& > .MuiTreeItem-content': {
            backgroundColor: `${colors.primary_1}70`,
        },
    },

    '& .three-state-checkbox': {
        position: 'absolute',
        right: '0px',
        padding: '0px',
        top: '0px',
    },

    '&.MuiTreeFocusInput .MuiTreeItem-content': {
        '&.Mui-focused': {
            backgroundColor: `${colors.primary_1}`,
        },
    },

    '& .MuiTreeItem-content': {
        '&:hover': {
            backgroundColor: colors.background_B2,
        },
        '&.Mui-focused': {
            backgroundColor: 'unset',
        },

        '&.Mui-disabled': {
            opacity: 1,
        },

        '& .MuiTreeItem-label': {
            height: '26px',
            lineHeight: '26px',

            '& .tree-item-icon': {
                position: 'absolute',
                left: '-12px',
            },
        },
    },

    '& svg': {
        color: colors.icon,
        fill: colors.icon,
    },
});

export type OnClickItemType = (item: TreeNodeType, e?: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
export type CustomLabelElementType = (item: TreeNodeType) => ReactNode;

export interface RenderTreeNodePropsType {
    treeNode: TreeNodeType;
    keysArr: string[];
    treeKey: string;
    defaultTitle?: string;
    nodeId?: string;
    renderSelect?: (
        value: ThreeStateValueType,
        treeNode: TreeNodeType,
        treeKey: string,
        keysArr?: string[]
    ) => ReactNode;
    onClickItem?: OnClickItemType;
    customLabelElement?: CustomLabelElementType;
    selected?: SelectedItemsType;
    selectedNodes: SelectedNodesType;
    translateLabel?: (keysArr: string[]) => string;
}

const RenderTreeNode: FC<RenderTreeNodePropsType> = (props) => {
    const childKeys = getChildKeys(props.treeNode);
    const hasChildren = childKeys.length > 0;
    const defaultNodeId = props.keysArr.join('.') || props.defaultTitle || 'main';

    if (props.treeNode.isEndpoint && !hasChildren) {
        const selectValue =
            (props.renderSelect &&
                getSelectValue({
                    treeNode: props.treeNode,
                    treeKey: props.treeKey,
                    selected: props.selected,
                    selectedNodes: props.selectedNodes,
                })) ||
            0;

        return (
            <TreeItem
                // disabled
                nodeId={props.nodeId || defaultNodeId}
                // @ts-ignore
                onClick={(e) => props.onClickItem && props.onClickItem(props.treeNode.data, e)}
                label={
                    <>
                        <div className="tree-item-icon">&#x2022;</div>
                        {props.translateLabel?.(props.keysArr) ||
                            props.keysArr[props.keysArr.length - 1] ||
                            props.keysArr.join('.') ||
                            props.defaultTitle}
                        {props.customLabelElement && props.customLabelElement(props.treeNode.data)}
                        {props.renderSelect && props.renderSelect(selectValue, props.treeNode, props.treeKey)}
                    </>
                }
                className={` tree-item-selected-${selectValue}`}
            />
        );
    }

    const selectValue =
        (props.renderSelect &&
            getSelectValue({
                treeNode: props.treeNode,
                treeKey: props.treeKey,
                selected: props.selected,
                selectedNodes: props.selectedNodes,
                keysArr: props.keysArr,
            })) ||
        0;

    return (
        <TreeItem
            nodeId={props.nodeId || defaultNodeId}
            label={
                <>
                    {props.translateLabel?.(props.keysArr) ||
                        props.keysArr[props.keysArr.length - 1] ||
                        props.keysArr.join('.') ||
                        props.defaultTitle}
                    {props.renderSelect &&
                        props.renderSelect(selectValue, props.treeNode, props.treeKey, props.keysArr)}
                </>
            }
            className={` tree-item-selected-${selectValue}`}
        >
            {props.treeNode.isEndpoint && (
                <RenderTreeNode
                    treeNode={{ data: props.treeNode.data, isEndpoint: true }}
                    treeKey={props.treeKey}
                    keysArr={props.keysArr}
                    nodeId={`${defaultNodeId}.*`}
                    renderSelect={props.renderSelect}
                    onClickItem={props.onClickItem}
                    customLabelElement={props.customLabelElement}
                    selected={props.selected}
                    selectedNodes={props.selectedNodes}
                    translateLabel={props.translateLabel}
                />
            )}
            {childKeys
                .sort((keyA, keyB) => {
                    if (props.treeNode[keyA].isEndpoint && !props.treeNode[keyB].isEndpoint) {
                        return 1;
                    }
                    if (!props.treeNode[keyA].isEndpoint && props.treeNode[keyB].isEndpoint) {
                        return -1;
                    }
                    return keyA > keyB ? 1 : -1;
                })
                .map((key) => (
                    <RenderTreeNode
                        key={key}
                        treeNode={props.treeNode[key]}
                        treeKey={props.treeKey}
                        keysArr={[...props.keysArr, key]}
                        renderSelect={props.renderSelect}
                        onClickItem={props.onClickItem}
                        customLabelElement={props.customLabelElement}
                        selected={props.selected}
                        selectedNodes={props.selectedNodes}
                        translateLabel={props.translateLabel}
                    />
                ))}
        </TreeItem>
    );
};

const recursiveSelectedNodes = (
    dataInTree: TreeNodeType,
    treeKey: string,
    arrKeys: string[],
    selectedNodes: SelectedNodesType,
    selected: SelectedItemsType
): SelectNodeValueType => {
    const childKeys = getChildKeys(dataInTree);

    if (dataInTree.isEndpoint && childKeys.length === 0) {
        return selected[dataInTree.data[treeKey]] ? [1, 0] : [0, 1];
    }

    const values: SelectNodeValueType = [0, 0];

    if (dataInTree.isEndpoint && dataInTree.data) {
        if (selected[dataInTree.data[treeKey]]) {
            values[0] += 1;
        } else {
            values[1] += 1;
        }
    }

    childKeys.forEach((key) => {
        const itemValues = recursiveSelectedNodes(
            dataInTree[key],
            treeKey,
            [...arrKeys, key],
            selectedNodes,
            selected
        );
        values[0] += itemValues[0];
        values[1] += itemValues[1];
    });
    selectedNodes[arrKeys.join('.')] = values;
    return values;
};

const getSelectedNodes = (dataInTree: TreeNodeType, treeKey: string, selected: SelectedItemsType) => {
    const selectedNodes: SelectedNodesType = {};

    recursiveSelectedNodes(dataInTree, treeKey, [], selectedNodes, selected);

    return selectedNodes;
};

export interface TreePropsType {
    data: ReceivedRowType[] | null;
    treeKey: string;
    defaultTitle: string;
    loading?: boolean;
    selected?: SelectedItemsType;
    setSelected?: (selected: SelectedItemsType) => void;
    disabledSelect?: boolean;
    onClickItem?: OnClickItemType;
    customLabelElement?: CustomLabelElementType;
    disabledItemsSelect?: SelectedItemsType;
    translateLabel?: (keysArr: string[]) => string;
    highlightFocusLines?: boolean;
    sx?: SxProps<Theme>;
}

const Tree: FC<TreePropsType> = (props) => {
    const [dataInTree, setDataInTree] = useState<TreeNodeType>(null);
    const [selectedNodes, setSelectedNodes] = useState<SelectedNodesType>({});
    const [expanded, setExpanded] = React.useState<string[]>([]);

    useEffect(() => {
        if (props.data) {
            const newDataInTree = arrToTreeStructure(props.data, props.treeKey);
            setDataInTree(newDataInTree);
            setExpanded([props.defaultTitle]);
        }
    }, [props.data]);

    useEffect(() => {
        if (dataInTree && props.selected) {
            setSelectedNodes(getSelectedNodes(dataInTree, props.treeKey, props.selected));
        }
    }, [dataInTree, props.selected]);

    const handleSelectItems = (newSelected: SelectedItemsType) => {
        if (props.setSelected) {
            if (props.disabledItemsSelect) {
                Object.keys(newSelected)
                    .filter((key) => props.disabledItemsSelect && props.disabledItemsSelect[key])
                    .forEach((key) => {
                        delete newSelected[key];
                    });
            }
            props.setSelected(newSelected);
        }
    };

    const handleClickItem: OnClickItemType = (item, e) => {
        if (props.onClickItem) {
            props.onClickItem(item, e);
        } else if (!props.disabledSelect && props.setSelected && props.selected) {
            const newSelected = { ...props.selected };
            if (newSelected[item[props.treeKey]]) {
                delete newSelected[item[props.treeKey]];
            } else {
                newSelected[item[props.treeKey]] = true;
            }

            handleSelectItems(newSelected);
        }
    };

    if (props.loading) {
        return (
            <MUIBox>
                <CircularProgress color="secondary" size={100} />
            </MUIBox>
        );
    }

    if (!dataInTree) {
        return null;
    }

    const handleToggle = (event: React.SyntheticEvent, nodeIds: string[]) => {
        setExpanded(nodeIds);
    };
    return (
        <TreeContainer className={props.highlightFocusLines ? 'MuiTreeFocusInput' : ''} sx={props.sx}>
            <TreeView
                defaultCollapseIcon={<FolderOpenIcon />}
                defaultExpandIcon={<FolderCopyIcon />}
                disableSelection
                // selected={[]}
                expanded={expanded}
                onNodeToggle={handleToggle}
            >
                {
                    <RenderTreeNode
                        treeNode={dataInTree}
                        keysArr={[]}
                        treeKey={props.treeKey}
                        defaultTitle={props.defaultTitle}
                        selected={props.selected}
                        selectedNodes={selectedNodes}
                        renderSelect={(value, treeNode, treeKey, keysArr) =>
                            props.selected &&
                            handleSelectItems &&
                            selectedNodes && (
                                <Select
                                    treeNode={treeNode}
                                    treeKey={treeKey}
                                    selected={props.selected}
                                    setSelected={handleSelectItems}
                                    keysArr={keysArr}
                                    disabled={props.disabledSelect}
                                    disabledItemsSelect={props.disabledItemsSelect}
                                    value={value}
                                />
                            )
                        }
                        onClickItem={handleClickItem}
                        customLabelElement={props.customLabelElement}
                        translateLabel={props.translateLabel}
                    />
                }
            </TreeView>
        </TreeContainer>
    );
};

export default Tree;
